/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import moment from 'moment';

import type { TranslatorProps } from 'react-i18next';

import type TypeActionDrawerStore from 'stores/ActionDrawerStore';

import Loading from 'components/Loading/Loading';

import Form from 'forms/_.extend';

import ActionDrawerHeader from 'views/ActionDrawer/components/ActionDrawerHeader/ActionDrawerHeader';

import ScheduleCalMultiForm from './ScheduleCalMultiForm/ScheduleCalMultiForm';
import ScheduleCalMultiSuccess from './ScheduleCalMultiSuccess/ScheduleCalMultiSuccess';
import ScheduleCalMultiError from './ScheduleCalMultiError/ScheduleCalMultiError';

import { isAdmin } from '../../.././../../permissions/general.js';

import styles from '../../../ActionDrawer.module.scss';

type TypeProps = {
  t: TranslatorProps,
  actionDrawerStore: TypeActionDrawerStore,
  assetStore: Object,
};

@translate()
@inject('actionDrawerStore', 'assetStore')
@observer
export default class ScheduleAssetCalMulti extends React.Component<*> {
  createForm = () => {
    const { t, actionDrawerStore, assetStore }: TypeProps = this.props;

    const asset: Object = this.props.formData;

    const fields: Array<Object> = [
      {
        name: 'collectionDetails',
        label: t('general:labels.collection_details'),
        fields: [
          {
            name: 'collectionDate',
            label: t('general:labels.collection_date'),
            rules: `required|date|after:${moment(new Date()).format('MMMM DD YYYY')}`,
            value: new Date(
              moment()
                .add(1, 'd')
                .format('YYYY-MM-DD')
            ),
          },
        ],
      },
      {
        name: 'additionalInformation',
        label: t('general:labels.additional_info'),
        fields: [
          {
            name: 'replacementRequired',
            label: t('general:assets.reasons.replacement_required'),
            rules: 'required|boolean',
            value: false,
          },
          {
            name: 'keepOriginalPackaging',
            label: t('general:assets.reasons.original_packaging') + '?',
            rules: 'required|boolean',
            value: false,
            related: ['additionalInformation.packagingDescription'],
          },
          {
            name: 'packagingDescription',
            label: t('general:labels.describe_packaging_details'),
            rules: 'required_if:additionalInformation.keepOriginalPackaging,false|string',
            value: '',
          },
        ],
      },
    ];

    fields[0].fields.push(
      {
        name: 'collectLocation',
        label: t('general:assets.labels.collect'),
        rules: 'required|string',
        value: 'CURRENT_LOCATION',
        options: [
          {
            id: 'CURRENT_LOCATION',
            label: t('general:labels.collect_from_this_location'),
            value: 'CURRENT_LOCATION',
          },
          {
            id: 'CONTRACT_LOCATION',
            label: t('general:labels.describe_packaging_details'),
            value: 'CONTRACT_LOCATION',
          },
        ],
      },
      {
        name: 'contactDetails',
        label: t('general:contact details'),
        rules: 'required|string',
        value: 'USER_CONTACT_DETAILS',
        options: [
          {
            id: 'USER_CONTACT_DETAILS',
            label: t('general:labels.use_my_contact_details'),
            value: 'USER_CONTACT_DETAILS',
          },
          {
            id: 'CONTRACT_PICKUP_DETAILS',
            label: t('general:contracts.labels.collect_from_pickup_location'),
            value: 'CONTRACT_PICKUP_DETAILS',
          },
        ],
      }
    );

    const hooks: Object = {
      onSuccess(data: Object) {
        const assetIds = asset.map(a => a.id);

        const { collectLocation, contactDetails, collectionDate } = data.values().collectionDetails;

        const {
          replacementRequired,
          keepOriginalPackaging,
          packagingDescription,
        } = data.values().additionalInformation;

        actionDrawerStore.scheduleAssetCalMulti({
          collection_date: collectionDate,
          product_id: 'null',
          collect_location: collectLocation,
          contact_details: contactDetails,
          replacement_required: replacementRequired,
          keep_original_packaging: keepOriginalPackaging,
          packaging_description: packagingDescription,
          fault_details: 'null',
          asset_ids: assetIds,
        });
      },
    };
    return new Form(
      { fields },
      {
        hooks,
        name: 'scheduleAssetCalMulti',
      }
    );
  };

  render(): React.Element<*> {
    const { t, actionDrawerStore }: TypeProps = this.props;

    const asset: Object = this.props.formData;

    const title: string = isAdmin()
      ? t('general:calibrations.schedule_calibrations')
      : t('general:calibrations.schedule_calibration_requests');

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={title} />
          <ScheduleCalMultiError asset={asset} />
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.success}`}>
          <ActionDrawerHeader title={title} />
          <ScheduleCalMultiSuccess response={actionDrawerStore.response} />
        </div>
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={title} />
        <ScheduleCalMultiForm asset={asset} form={this.createForm()} />
      </div>
    );
  }
}
